import { getCounterpartyViewRowId } from "@common/helpers/counterpartyViewRowId";
import {
    InvoicesByCounterpartyItem,
    InvoiceByCounterpartySortValue,
} from "@common/types/invoice";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { InvoicesByCustomerView as InvoicesByCustomerViewType } from "@common/types/views/invoiceViews";
import { View } from "@common/types/views/views";
import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { invoicesByCustomerViewConfig } from "./invoicesByCustomerViewConfig";
import styles from "./InvoicesByCustomerView.module.scss";

interface Props {
    view: InvoicesByCustomerViewType;
    invoicesByCustomer: InvoicesByCounterpartyItem[] | null;
    sort?: InvoiceByCounterpartySortValue;
    onSortChange?: (sort: InvoiceByCounterpartySortValue) => void;
    onDrilldown?: (params: InvoiceFilters) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
}

export const InvoicesByCustomerView: React.FC<Props> = ({
    view,
    invoicesByCustomer,
    sort,
    onSortChange,
    onDrilldown,
    emptyView = () => "No customer data found",
    footer,
    className,
}) => {
    const data = useMemo(() => {
        if (!invoicesByCustomer) {
            return null;
        }
        // Map the data to include the required 'id' field
        return invoicesByCustomer.map((item) => ({
            ...item,
            id: getCounterpartyViewRowId(item.counterpartyId),
        }));
    }, [invoicesByCustomer]);

    const handleDrilldown = useCallback(
        (params: InvoiceFilters) => {
            onDrilldown?.(params);
        },
        [onDrilldown],
    );

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onDrilldown: handleDrilldown,
            },
        }),
        [handleDrilldown],
    );

    return (
        <ViewTable
            className={classNames(styles.invoicesByCustomerView, className)}
            data={data}
            view={view as View}
            config={invoicesByCustomerViewConfig as any}
            sort={sort}
            onSortChange={onSortChange}
            fixedTopOffsetPx
            emptyView={emptyView}
            tableMeta={tableMeta}
            footer={footer}
        />
    );
};
